import React from 'react';
import {
  Box,
  Paper,
  Typography
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import logo from './../images/Logo Tsahal Conection.png';

// Imports des composants et hooks personnalisés
import {
  THEME_COLORS,
  TYPOGRAPHY,
  ANIMATIONS,
  useActivities,
  useParticipants,
  useModalForm,
  useSubmission,
  ActivitySelector,
  SelectedActivityDisplay,
  ParticipantsList,
  ActionButtons,
  ParticipantModal,
  ResultsPage
} from '../components/InscriptionActivites';

// Import des styles modernes
import '../components/InscriptionActivites/InscriptionActivites.css';

/**
 * Page principale d'inscription aux activités
 */
const InscriptionActivites = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Hooks personnalisés pour la gestion des activités
  const {
    activities,
    selectedActivity,
    loading,
    error,
    retryFetch,
    selectActivity,
    resetSelection,
    getSelectedActivity
  } = useActivities();

  // Hooks personnalisés pour la gestion des participants
  const {
    participants,
    addParticipant,
    removeParticipant,
    clearParticipants,
    createAccompanistParticipant,
    createSoldierParticipantFromLead,
    createSoldierParticipantManual
  } = useParticipants();

  // Hooks personnalisés pour le modal
  const {
    modalOpen,
    participantType,
    modalStep,
    leadFound,
    searchingLead,
    formErrors,
    accompanistData,
    soldierData,
    openModal,
    closeModal,
    goToStep,
    selectParticipantType,
    setErrors,
    setLeadSearching,
    setFoundLead,
    handleAccompanistDataChange,
    handleSoldierDataChange
  } = useModalForm();

  // Hooks personnalisés pour la soumission
  const {
    isSubmitting,
    submissionResults,
    showResults,
    submitAllParticipants,
    startNewInscription,
    getSubmissionStats,
    getTitleMessage,
    getTitleColor
  } = useSubmission();

  /**
   * Gestion de l'ajout d'un participant
   */
  const handleAddParticipant = (type, foundLead = null) => {
    if (type === 'accompagnateur') {
      const participant = createAccompanistParticipant(accompanistData);
      addParticipant(participant);
      closeModal();
    } else if (type === 'futur_soldat') {
      // Utilise le lead passé en paramètre OU celui du state
      const leadToUse = foundLead || leadFound;
      
      if (leadToUse) {
        const participant = createSoldierParticipantFromLead(leadToUse, soldierData.email);
        addParticipant(participant);
        closeModal();
      } else {
        const participant = createSoldierParticipantManual(soldierData);
        addParticipant(participant);
        closeModal();
      }
    }
  };

  /**
   * Gestion de la soumission finale
   */
  const handleSubmitAll = () => {
    submitAllParticipants(participants, selectedActivity, (error) => {
      console.error('Erreur de soumission:', error);
    });
  };

  /**
   * Nouvelle inscription
   */
  const handleNewInscription = () => {
    startNewInscription(clearParticipants, resetSelection);
  };

  // Affichage de la page des résultats
  if (showResults && submissionResults) {
    return (
      <ResultsPage
        submissionResults={submissionResults}
        onNewInscription={handleNewInscription}
        getTitleMessage={getTitleMessage}
        getTitleColor={getTitleColor}
        getSubmissionStats={getSubmissionStats}
      />
    );
  }

  return (
    <Box
      className="inscription-activites"
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${THEME_COLORS.background.main} 0%, ${THEME_COLORS.background.dark} 100%)`,
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1, sm: 2, md: 3 },
        position: 'relative'
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: '100%', sm: 600, md: 800, lg: 900 },
          mx: 'auto',
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Titre responsive et plus fin */}
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 3, sm: 4, md: 5 },
            px: { xs: 1, sm: 2 }
          }}
        >
          <img src={logo} alt="Logo" style={{ height: '70px', width: '70px' }} />
          <Typography 
            variant="h1"
            component="h1"
            sx={{ 
              fontSize: { 
                xs: TYPOGRAPHY.fontSize['2xl'], 
                sm: TYPOGRAPHY.fontSize['2xl'], 
                md: TYPOGRAPHY.fontSize['3xl'] 
              },
              fontWeight: TYPOGRAPHY.fontWeight.bold,
              fontFamily: TYPOGRAPHY.fontFamily.primary,
              background: THEME_COLORS.primary.gradient,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textAlign: 'center',
              mb: { xs: 1, sm: 1.5 },
              letterSpacing: '-0.02em',
              lineHeight: 1.2
            }}
          >
            Inscription aux Activités
          </Typography>
          
          <Typography 
            variant="h6"
            sx={{ 
              color: THEME_COLORS.text.secondary,
              fontFamily: TYPOGRAPHY.fontFamily.primary,
              fontWeight: TYPOGRAPHY.fontWeight.medium,
              fontSize: { xs: TYPOGRAPHY.fontSize.sm, sm: TYPOGRAPHY.fontSize.base },
              maxWidth: { xs: '100%', sm: 400, md: 500 },
              mx: 'auto',
              lineHeight: 1.4
            }}
          >
            Rejoignez nos événements et conférences exclusives
          </Typography>
        </Box>

        {/* Container principal responsive */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: { xs: 2, sm: 3, md: 4 },
            background: THEME_COLORS.background.glass,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${THEME_COLORS.border.light}`,
            boxShadow: THEME_COLORS.shadow.md,
            transition: ANIMATIONS.transition.normal
          }}
        >

        {/* Sélection de l'activité */}
        <ActivitySelector
          activities={activities}
          selectedActivity={selectedActivity}
          loading={loading}
          error={error}
          onActivityChange={selectActivity}
          onRetry={retryFetch}
        />


        {/* Affichage des participants */}
        <ParticipantsList 
          participants={participants}
          onRemoveParticipant={removeParticipant}
        />

        {/* Boutons d'action */}
        <ActionButtons
          selectedActivity={selectedActivity}
          participantsCount={participants.length}
          isSubmitting={isSubmitting}
          onAddParticipant={openModal}
          onSubmitAll={handleSubmitAll}
        />

        {/* Modal d'ajout de participants */}
        <ParticipantModal
          modalOpen={modalOpen}
          onClose={closeModal}
          participantType={participantType}
          modalStep={modalStep}
          leadFound={leadFound}
          searchingLead={searchingLead}
          formErrors={formErrors}
          accompanistData={accompanistData}
          soldierData={soldierData}
          onParticipantTypeChange={selectParticipantType}
          onAccompanistDataChange={handleAccompanistDataChange}
          onSoldierDataChange={handleSoldierDataChange}
          onAddParticipant={handleAddParticipant}
          setErrors={setErrors}
          setLeadSearching={setLeadSearching}
          setFoundLead={setFoundLead}
          goToStep={goToStep}
        />
        </Paper>
      </Box>
    </Box>
  );
};

export default InscriptionActivites;